/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useRef, useState } from 'react';

import { Typography2 } from '@coursera/cds-core';
import { CloseIcon, MaximizeIcon, MinimizeIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import { CoachIconButton } from 'bundles/ai-coach-platform/components/building-blocks';
import { clearChatSession, dropMessages } from 'bundles/ai-coach-platform/components/patterns/chat/store/dispatcher';
import CloseCoachChatConfirmationDialog from 'bundles/unified-common/components/coach-on-discovery/CloseCoachChatConfirmationDialog';
import SparkleIcon from 'bundles/unified-common/components/coach-on-discovery/SparkleIcon';
import { useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';

import _t from 'i18n!nls/unified-common';

const styles = {
  container: css`
    padding: var(--cds-spacing-100) var(--cds-spacing-200);
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,
  brandingContainer: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-50);
  `,
};

const MinimizedCoachChatHeader = () => {
  const { setCoachOpen, contextConfig, isMinimizedChat, toggleFullScreen } = useCoachState();
  const [dialogOpen, setDialogOpen] = useState(false);
  const documentBodyOverflowValue = useRef<string | null>(null);
  const track = useTracker();

  const handleClose = () => {
    track('interact_coach', {
      coachInteractionType: 'close_coach_tool',
      coach: {
        mode: contextConfig.config.coachModeId,
        location: 'toolbar',
      },
    });
    /**
     * Since CDS Dialog resets body overflow to the value before dialog open,
     * manually set overflow to visible here to resolve body overflow is set
     * to hidden if closing the chat when chat is in fullscreen.
     */
    documentBodyOverflowValue.current = document.body.style.overflow;
    document.body.style.overflow = 'visible';
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    track('interact_coach', {
      coachInteractionType: 'end_chat_session',
      coach: {
        mode: contextConfig.config.coachModeId,
      },
      coachAction: 'CLOSE_CHAT',
    });
    setDialogOpen(false);
    setCoachOpen(false);
    // Close chat will clear chat history
    clearChatSession();
    dropMessages();
    if (isMinimizedChat) toggleFullScreen();
  };

  const handleDialogKeepChat = () => {
    track('interact_coach', {
      coachInteractionType: 'end_chat_session',
      coach: {
        mode: contextConfig.config.coachModeId,
      },
      coachAction: 'KEEP_CHAT_WHILE_BROWSE',
    });
    // Keep chat will close dialog and keep the chat, so reset overflow value to actual previous state
    setDialogOpen(false);
    document.body.style.overflow = documentBodyOverflowValue.current || 'visible';
    if (!isMinimizedChat) toggleFullScreen();
  };

  return (
    <div css={styles.container}>
      <div css={styles.brandingContainer}>
        <SparkleIcon size="small" />
        <Typography2 component="h2" variant="subtitleSmall">
          {_t('Ask Coursera')}
        </Typography2>
      </div>
      <div>
        <CoachIconButton
          size="small"
          icon={isMinimizedChat ? <MaximizeIcon color="default" /> : <MinimizeIcon color="default" />}
          aria-label={isMinimizedChat ? _t('Expand chat') : _t('Collapse chat')}
          tooltip={isMinimizedChat ? _t('Expand chat') : _t('Collapse chat')}
          tooltipPlacement="bottom"
          onClick={toggleFullScreen}
          data-testid="coach-full-screen"
        />
        <CoachIconButton
          size="small"
          icon={<CloseIcon color="default" />}
          aria-label={_t('Close chat')}
          tooltip={_t('Close chat')}
          tooltipPlacement="bottom"
          onClick={handleClose}
          data-testid="coach-close"
        />
      </div>
      <CloseCoachChatConfirmationDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onKeepChat={handleDialogKeepChat}
      />
    </div>
  );
};

export default MinimizedCoachChatHeader;
